import { parseAppointmentTime } from "@/utils/appointmentTimezone/appointmentTimezone";

export type DmhAppointment = {
  appointmentId?: string;
  appointmentType?: string;
  appointmentStatus?: string;
  appointment_status?: string;
  status?: string;
  service_slug?: string;
  serviceSlug?: string;
  serviceName?: string;
  clinician?: { name?: string } | null;
  startTime?: string | null;
  endTime?: string | null;
};

export type TimedDmhAppointment = DmhAppointment & {
  startDublinMs: number;
  endDublinMs: number;
};

export type CalendarEvent = {
  uid: string;
  title: string;
  description: string;
  url: string;
  startMs: number;
  endMs: number;
  createdMs: number;
};

// Only mental health bookings belong on the EAP page; get-all-appointments also returns GP and other services.
const EAP_APPOINTMENT_SERVICE_SLUGS = ["sl_dmh", "dmh"];

// Mirrors SideBar: an appointment stays joinable until it becomes a did-not-attend.
const DID_NOT_ATTEND_CUTOFF_MS = 15 * 60 * 1000;

const FALLBACK_DURATION_MS = 30 * 60 * 1000;

// RFC 5545 caps content lines at 75 octets; longer ones continue on a line starting with a space.
const CALENDAR_LINE_OCTETS = 75;

export const withAppointmentInstants = (
  appointments: DmhAppointment[],
): TimedDmhAppointment[] =>
  appointments.map((appointment) => {
    const startTime = parseAppointmentTime(appointment.startTime);
    const endTime = parseAppointmentTime(appointment.endTime);
    return {
      ...appointment,
      startDublinMs: startTime ? startTime.valueOf() : 0,
      endDublinMs: endTime ? endTime.valueOf() : 0,
    };
  });

export const isCancelledAppointment = (
  appointment: DmhAppointment,
): boolean => {
  const status = String(
    appointment.appointmentStatus ||
      appointment.appointment_status ||
      appointment.status ||
      "",
  )
    .trim()
    .toLowerCase();
  return status === "cancelled" || status === "canceled";
};

export const isEapAppointment = (appointment: DmhAppointment): boolean => {
  const serviceSlug = String(
    appointment.service_slug || appointment.serviceSlug || "",
  )
    .trim()
    .toLowerCase();
  return EAP_APPOINTMENT_SERVICE_SLUGS.includes(serviceSlug);
};

export const selectUpcomingEapAppointments = (
  appointments: TimedDmhAppointment[],
  nowMs = Date.now(),
): TimedDmhAppointment[] =>
  appointments
    .filter(
      (appointment) =>
        appointment.startDublinMs > 0 &&
        nowMs <= appointment.startDublinMs + DID_NOT_ATTEND_CUTOFF_MS &&
        isEapAppointment(appointment) &&
        !isCancelledAppointment(appointment),
    )
    .sort((first, second) => first.startDublinMs - second.startDublinMs);

export const appointmentEndMs = (appointment: TimedDmhAppointment): number =>
  appointment.endDublinMs > appointment.startDublinMs
    ? appointment.endDublinMs
    : appointment.startDublinMs + FALLBACK_DURATION_MS;

const toCalendarTimestamp = (instantMs: number): string =>
  new Date(instantMs)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");

const escapeCalendarText = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

const utf8Length = (character: string): number => {
  const codePoint = character.codePointAt(0) ?? 0;
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4;
};

const foldCalendarLine = (line: string): string => {
  const segments: string[] = [];
  let segment = "";
  let segmentOctets = 0;
  for (const character of Array.from(line)) {
    const characterOctets = utf8Length(character);
    // Continuation lines spend one octet on their leading space.
    const limit = segments.length
      ? CALENDAR_LINE_OCTETS - 1
      : CALENDAR_LINE_OCTETS;
    if (segmentOctets + characterOctets > limit) {
      segments.push(segment);
      segment = "";
      segmentOctets = 0;
    }
    segment += character;
    segmentOctets += characterOctets;
  }
  segments.push(segment);
  return segments.join("\r\n ");
};

export const buildCalendarEvent = ({
  uid,
  title,
  description,
  url,
  startMs,
  endMs,
  createdMs,
}: CalendarEvent): string =>
  [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Spectrum.Life//EAP//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toCalendarTimestamp(createdMs)}`,
    `DTSTART:${toCalendarTimestamp(startMs)}`,
    `DTEND:${toCalendarTimestamp(endMs)}`,
    `SUMMARY:${escapeCalendarText(title)}`,
    `DESCRIPTION:${escapeCalendarText(description)}`,
    `URL:${url}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .map(foldCalendarLine)
    .join("\r\n")
    .concat("\r\n");
